// src/toolkitServices/customerTransactionApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const TAG = {
  LIST: "LIST",
  cust: (customerId) => `CUST-${customerId}`,
  ledger: (ledgerId) => `LEDGER-${ledgerId}`,
};

// ✅ helper: normalize rows extraction across different API shapes
const extractRows = (result) => {
  if (!result) return [];
  if (Array.isArray(result?.rows)) return result.rows;
  if (Array.isArray(result?.data?.rows)) return result.data.rows;
  if (Array.isArray(result?.data)) return result.data;
  return [];
};

// ✅ helper: resolve ledgerId from different mutation responses
const resolveLedgerId = (result) => {
  const data = result?.data ?? result;
  return (
    data?._id ||
    data?.ledgerId ||
    data?.id ||
    data?.data?.ledgerId ||
    data?.data?.ledger?._id ||
    null
  );
};

// ✅ helper: resolve customerId from response (optional but useful for invalidation)
const resolveCustomerId = (result) => {
  const data = result?.data ?? result;
  return data?.customerId || data?.data?.customerId || null;
};

const customerTransactionApi = createApi({
  reducerPath: "customerTransactionApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),
  tagTypes: ["CustomerTransaction"],

  endpoints: (builder) => ({
    // 📋 Get Customer Transactions (with filters)
    // type can be: "ALL" | "SCHEME" | "INVOICE" | ...
    getCustomerTransactions: builder.query({
      query: ({
        page = 1,
        limit = 25,
        customerId,
        from,
        to,
        branchId,
        type,
        q,
      } = {}) => ({
        url: `/customers/transactions`,
        method: "POST",
        body: {
          page,
          limit,
          customerId: customerId || undefined,
          branchId: branchId || undefined,
          type: type || undefined,
          fromDate: from || undefined,
          toDate: to || undefined,
          q: q || undefined,
        },
      }),

      providesTags: (result, error, arg) => {
        const tags = [{ type: "CustomerTransaction", id: TAG.LIST }];

        if (arg?.customerId) {
          tags.push({ type: "CustomerTransaction", id: TAG.cust(arg.customerId) });
        }

        const rows = extractRows(result);
        for (const r of rows) {
          const ledgerId = r?._id || r?.ledgerId || r?.id;
          if (ledgerId) tags.push({ type: "CustomerTransaction", id: TAG.ledger(ledgerId) });
        }

        return tags;
      },
    }),

    // 🧾 Receive payment / add receipt entry
    // kind: "INVOICE" | "WALLET_ADD" | "WALLET_WITHDRAW" | "SCHEME" | "SCHEME_WITHDRAW"
    addCustomerReceipt: builder.mutation({
      query: ({
        customerId,
        branchId,
        kind,
        mode,
        amount,
        refNo,
        note,

        // INVOICE
        allocations,

        // SCHEME (AMOUNT or GRAM)
        enrollmentId,
        installmentNo, // optional
        grams, // GRAM scheme: grams (calculated on UI or backend)
        ratePerGram, // GRAM scheme: rate used to convert amount->grams

        // SCHEME_WITHDRAW
      } = {}) => ({
        url: `/customers/transactions/receipt`,
        method: "POST",
        body: {
          customerId,
          branchId: branchId || undefined,
          kind,
          mode,
          amount, // GRAM scheme me backend amount=0 allow karega

          refNo,
          note,

          // Invoice allocations only when kind = INVOICE
          allocations:
            kind === "INVOICE" && allocations?.length ? allocations : undefined,

          // Scheme link only for scheme actions
          enrollmentId:
            kind === "SCHEME" || kind === "SCHEME_WITHDRAW"
              ? enrollmentId || undefined
              : undefined,

          // ✅ installmentNo only for SCHEME
          installmentNo:
            kind === "SCHEME"
              ? installmentNo
                ? installmentNo
                : undefined
              : undefined,

          // ✅ GRAM extras only for SCHEME
          grams:
            kind === "SCHEME" ? (grams != null ? grams : undefined) : undefined,
          ratePerGram:
            kind === "SCHEME"
              ? ratePerGram != null
                ? ratePerGram
                : undefined
              : undefined,

          // ✅ payoutToWallet only for withdraw
          // FIX: Wallet mode => payoutToWallet true
          payoutToWallet:
            kind === "SCHEME_WITHDRAW" ? String(mode) === "Wallet" : undefined,
        },
      }),

      invalidatesTags: (result, error, arg) => {
        const ledgerId = resolveLedgerId(result);
        const respCustomerId = resolveCustomerId(result);

        const tags = [{ type: "CustomerTransaction", id: TAG.LIST }];

        const customerId = arg?.customerId || respCustomerId;
        if (customerId) tags.push({ type: "CustomerTransaction", id: TAG.cust(customerId) });

        if (ledgerId) tags.push({ type: "CustomerTransaction", id: TAG.ledger(ledgerId) });

        return tags;
      },
    }),

    // 🔍 Get Single Transaction Detail
    getTransactionDetail: builder.query({
      query: ({ customerId, ledgerId }) =>
        `/customers/${customerId}/ledger/${ledgerId}/detail`,

      providesTags: (result, error, arg) =>
        arg?.ledgerId
          ? [{ type: "CustomerTransaction", id: TAG.ledger(arg.ledgerId) }]
          : [{ type: "CustomerTransaction", id: TAG.LIST }],
    }),

    // 📲 Resend receipt SMS
    sendReceiptSms: builder.mutation({
      query: ({ ledgerId }) => ({
        url: `/customers/transactions/${ledgerId}/send-sms`,
        method: "POST",
      }),

      invalidatesTags: (result, error, arg) =>
        arg?.ledgerId
          ? [{ type: "CustomerTransaction", id: TAG.ledger(arg.ledgerId) }]
          : [{ type: "CustomerTransaction", id: TAG.LIST }],
    }),

    // 🧹 Reverse / Void Transaction (safe alternative to delete)
    // backend: POST /customers/transactions/:ledgerId/reverse
    reverseCustomerTransaction: builder.mutation({
      query: ({ ledgerId, reason, note } = {}) => ({
        url: `/customers/transactions/${ledgerId}/reverse`,
        method: "POST",
        body: {
          reason: reason || undefined,
          note: note || undefined,
        },
      }),

      invalidatesTags: (result, error, arg) => {
        const respLedgerId = resolveLedgerId(result);
        const respCustomerId = resolveCustomerId(result);

        const tags = [{ type: "CustomerTransaction", id: TAG.LIST }];

        const ledgerId = arg?.ledgerId || respLedgerId;
        if (ledgerId) tags.push({ type: "CustomerTransaction", id: TAG.ledger(ledgerId) });

        if (respCustomerId) tags.push({ type: "CustomerTransaction", id: TAG.cust(respCustomerId) });

        return tags;
      },
    }),

    // ✅ Create Sales Return (against invoice)
    createSalesReturn: builder.mutation({
      query: ({
        customerId,
        invoiceId,
        branchId,
        returnAmount,
        note,
        mode,
        refNo,
      } = {}) => ({
        url: `/customers/transactions/sales-return`,
        method: "POST",
        body: {
          customerId,
          invoiceId,
          branchId: branchId || undefined,
          returnAmount,
          note,
          mode,
          refNo,
        },
      }),

      invalidatesTags: (result, error, arg) => {
        const ledgerId = resolveLedgerId(result);
        const respCustomerId = resolveCustomerId(result);

        const tags = [{ type: "CustomerTransaction", id: TAG.LIST }];

        const customerId = arg?.customerId || respCustomerId;
        if (customerId) tags.push({ type: "CustomerTransaction", id: TAG.cust(customerId) });

        if (ledgerId) tags.push({ type: "CustomerTransaction", id: TAG.ledger(ledgerId) });

        return tags;
      },
    }),

    /* ------------------------------------------------------------------ */
    /* ✅ Optional convenience endpoints (keep if UI uses them)            */
    /* ------------------------------------------------------------------ */

    // 💰 Scheme Payment Receive (AMOUNT or GRAM)
    // NOTE: prefer using addCustomerReceipt with kind="SCHEME"
    addSchemePayment: builder.mutation({
      query: ({
        customerId,
        branchId,
        enrollmentId,
        installmentNo, // optional
        mode,

        // AMOUNT scheme
        amount,

        // GRAM scheme
        grams,
        ratePerGram,

        refNo,
        note,
      }) => ({
        url: `/customers/transactions/receipt`,
        method: "POST",
        body: {
          customerId,
          branchId: branchId || undefined,
          kind: "SCHEME",
          enrollmentId,
          installmentNo: installmentNo || undefined,
          mode,

          amount, // GRAM me 0 bhi ja sakta (backend allow) or actual amount bhi
          grams: grams != null ? grams : undefined,
          ratePerGram: ratePerGram != null ? ratePerGram : undefined,

          refNo,
          note,
        },
      }),

      invalidatesTags: (res, err, arg) => {
        const ledgerId = resolveLedgerId(res);
        const respCustomerId = resolveCustomerId(res);

        const tags = [{ type: "CustomerTransaction", id: TAG.LIST }];

        const customerId = arg?.customerId || respCustomerId;
        if (customerId) tags.push({ type: "CustomerTransaction", id: TAG.cust(customerId) });

        if (ledgerId) tags.push({ type: "CustomerTransaction", id: TAG.ledger(ledgerId) });

        return tags;
      },
    }),

    // 🏦 Scheme Withdraw
    // NOTE: prefer using addCustomerReceipt with kind="SCHEME_WITHDRAW"
    withdrawScheme: builder.mutation({
      query: ({
        customerId,
        branchId,
        enrollmentId,
        mode,
        amount,
        refNo,
        note,
      }) => ({
        url: `/customers/transactions/receipt`,
        method: "POST",
        body: {
          customerId,
          branchId: branchId || undefined,
          kind: "SCHEME_WITHDRAW",
          enrollmentId,
          mode,
          amount,
          refNo,
          note,

          // ✅ FIX: Wallet mode => payoutToWallet true
          payoutToWallet: String(mode) === "Wallet",
        },
      }),

      invalidatesTags: (res, err, arg) => {
        const ledgerId = resolveLedgerId(res);
        const respCustomerId = resolveCustomerId(res);

        const tags = [{ type: "CustomerTransaction", id: TAG.LIST }];

        const customerId = arg?.customerId || respCustomerId;
        if (customerId) tags.push({ type: "CustomerTransaction", id: TAG.cust(customerId) });

        if (ledgerId) tags.push({ type: "CustomerTransaction", id: TAG.ledger(ledgerId) });

        return tags;
      },
    }),
  }),
});

export const {
  useGetCustomerTransactionsQuery,
  useLazyGetCustomerTransactionsQuery,

  useAddCustomerReceiptMutation, // covers SCHEME + SCHEME_WITHDRAW too
  useGetTransactionDetailQuery,
  useSendReceiptSmsMutation,

  // ✅ new reverse hook
  useReverseCustomerTransactionMutation,

  useCreateSalesReturnMutation,

  // optional convenience hooks
  useAddSchemePaymentMutation,
  useWithdrawSchemeMutation,
} = customerTransactionApi;

export default customerTransactionApi;
